import React, { useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useLang } from '../context/LangContext';

const supported = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== 'undefined';

export const MicButton = ({ onText, label, className = '', testId = 'mic-btn', size = 'sm' }) => {
  const { lang, t } = useLang();
  const [state, setState] = useState('idle');
  const rec = useRef(null);
  const chunks = useRef([]);
  if (!supported) return null;

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const r = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      r.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        setState('busy');
        const blob = new Blob(chunks.current, { type: mime });
        const fd = new FormData();
        fd.append('file', blob, mime.includes('webm') ? 'speech.webm' : 'speech.mp4');
        fd.append('lang', lang);
        try { const { data } = await api.post('/stt', fd); if (data.text) onText(data.text); else toast.error(t('Nothing heard — try again')); }
        catch (e) { toast.error(e.response?.data?.detail || t('Could not transcribe')); }
        finally { setState('idle'); }
      };
      r.start();
      rec.current = r;
      setState('rec');
    } catch { toast.error(t('Microphone permission is needed to speak your answer')); }
  };
  const stop = () => { rec.current?.stop(); };

  return (
    <Button type="button" size={size} variant={state === 'rec' ? 'default' : 'outline'} onClick={state === 'rec' ? stop : state === 'idle' ? start : undefined} disabled={state === 'busy'}
      aria-pressed={state === 'rec'} aria-label={state === 'rec' ? t('Stop recording') : t('Speak')} data-testid={testId}
      className={`gap-1.5 ${state === 'rec' ? 'bg-[#C26227] hover:bg-[#C26227]/90 text-white animate-pulse' : ''} ${className}`}>
      {state === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : state === 'rec' ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      {label !== null && (state === 'rec' ? t('Stop') : state === 'busy' ? t('Transcribing…') : (label || t('Speak')))}
    </Button>
  );
};
